import _ from 'lodash';
import { IForm } from '@ts/ui-models';
import Misc from '@components/Misc';
import SensorDataGrid from '@pages/DataBase/Sensors/SensorDataGrid';
import InterfacesList from './DeviceInterfaces/InterfacesList';
import PositionGenerationPoliciesSelector from './PositionGenerationPoliciesSelector';
import { WithTranslation, withTranslation } from 'react-i18next';
import { WrapperEntitySelector, JsonInput } from '@components/Inputs';
import { Grid, TextInput, Modal, Select, Typography, Checkbox, Icon, Tooltip } from '@ts/storybook';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { IAppProps } from '@interfaces/App.interface';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';

interface IDeviceFormProps extends IForm, IAppProps, WithTranslation {
  isItemType?: boolean;
}

const DeviceForm = (props: IDeviceFormProps) => {
  const { t, mode, values, onCallbackChange, isItemType } = props;
  const { deviceTypeId } = values;

  const isViewMode = mode === 'view';
  const isDeviceItemType = Boolean(isItemType && deviceTypeId);
  const showNewSensors = (mode === 'new' || isDeviceItemType) && deviceTypeId;
  const showDeviceSensors = !isDeviceItemType && mode !== 'new';
  const showLocation = !isDeviceItemType && deviceTypeId;

  //#region Functions
  const onChangeCoordinates = (objectName: 'localCoordinates' | 'GPSCoordinates', prop) => value => {
    let obj = { ...values[objectName] };
    obj[prop] = value;
    return onCallbackChange(objectName)(obj);
  };
  //#endregion

  //#region Handlers
  const handleChangeOperation = (idx, availableOperations) => {
    return value => {
      const { sensors } = props.values;
      const selectedOperation = availableOperations.find(aO => aO.logicalId === value);
      _.set(sensors[idx], 'selectedOperation', selectedOperation);
      _.set(sensors[idx], 'streamParams', selectedOperation.params);
      onCallbackChange('sensors')(sensors);
    };
  };

  const handleChangeParam = idx => {
    return value => {
      let sensors = _.cloneDeep(props.values.sensors);
      _.set(sensors[idx], 'streamParams', value);
      onCallbackChange('sensors')(sensors);
    };
  };

  const handleChangeDeviceType = (entity, entityName, fkField) => {
    if (entity) {
      // state should not be mutated!!!!
      const sensors = _.cloneDeep(entity).xDtRt?.map(xDtRt => {
        const sensorName = (xDtRt.params && xDtRt.params.sensorName) || xDtRt.readingType.name;
        const readingType = xDtRt.readingType;
        const availableOperations = xDtRt.readingType.xOperationReadingTypes.map(xOrT => {
          const operation = xOrT.operation;
          operation.params = xOrT.params;
          return operation;
        });

        const defaultOperation = availableOperations.find(op => op.logicalId === 'equal');

        let streamParams;
        if (defaultOperation) {
          streamParams = defaultOperation.params;
        }
        if (!streamParams) {
          streamParams = xDtRt.params;
        }

        return {
          name: sensorName,
          readingType,
          selectedOperation: defaultOperation,
          availableOperations,
          streamParams,
          params: xDtRt.params.params,
        };
      });
      onCallbackChange('sensors')(sensors);
    }

    handleChangeEntityDeviceTypeSelector(entity, entityName, fkField);
  };

  const handleChangeEntityDeviceTypeSelector = (entity, entityName, fkField) => {
    const value = entity ? entity.getID() : null;
    onCallbackChange(entityName)(entity);
    onCallbackChange(fkField)(value);
  };
  //#endregion

  //#region Render
  const renderAutoSelfAttach = () => {
    const { deviceType, autoSelfAttach } = values;
    const disabled = !isDeviceItemType || !deviceType || mode === 'edit';
    const isReadOnly = mode === 'view';
    return (
      <Grid container>
        <Grid size={'auto'}>
          <Checkbox
            id="autoSelfAttach"
            checked={Boolean(autoSelfAttach)} //value === 'on'
            disabled={!isReadOnly && disabled}
            isReadOnly={isReadOnly}
            label="Auto self-attach"
            onChangeValue={onCallbackChange('autoSelfAttach')}
          />
        </Grid>
        <Grid className="mt-[7px]">
          <Tooltip title="Attach the device to itself upon creation">
            <Icon icon="action-info" />
          </Tooltip>
        </Grid>
        {!isItemType && (
          <Grid size={12} className="h-0">
            <Typography variant="body2" marginBottom={4} className="text-[#999]" text={[<b>Only configurable in item type</b>]} />
          </Grid>
        )}
      </Grid>
    );
  };

  const renderPositionGenerationPolicies = () => {
    const { deviceType } = values;
    const pgps = _.get(deviceType, 'pgps', []);
    const disabled = !isItemType || !deviceType || mode === 'view';
    return (
      <>
        <PositionGenerationPoliciesSelector
          positionGenerationPolicies={pgps}
          value={_.get(values, 'typeParams.positionGenerationPolicyId', '')}
          onChange={onCallbackChange('positionGenerationPolicyId')}
          disabled={disabled}
        />
        {!isItemType && (
          <Typography
            variant="body2"
            marginBottom={4}
            className="text-[#999] h-0"
            text={[<b>Only configurable in item type</b>]}
          />
        )}
      </>
    );
  };

  const renderSensorTable = () => {
    const nonVirtualSensors = values.sensors?.filter(sensor => sensor && sensor.params.isVirtual === false) || [];
    if (!nonVirtualSensors) return null;
    return <SensorDataGrid sensors={nonVirtualSensors} />;
  };

  const renderIdentifiers = () => {
    const { elementTypeId } = values;
    const cantModified = (elementTypeId && mode === 'new') || mode === 'view' || mode === 'edit';

    return (
      <Grid container spacing={1} direction="column">
        <Typography variant="h5" marginBottom={4} text={[<b>Main information</b>]} />
        <WrapperEntitySelector
          label={'Device type'}
          searchComponent={generateSearchEntitySelector('deviceType')}
          value={values.deviceType ?? values.deviceTypeId}
          onChangeEntity={entity => handleChangeDeviceType(entity, 'deviceType', 'deviceTypeId')}
          isReadOnly={cantModified}
        />
        {renderAutoSelfAttach()}
        {renderPositionGenerationPolicies()}
        <TextInput isReadOnly label="Manufacturer" value={_.get(values, 'deviceType.manufacturer', null)} />
        <TextInput isReadOnly label="Part number" value={_.get(values, 'deviceType.partNumber', null)} />
      </Grid>
    );
  };

  const renderNewInterfaces = () => {
    if (props.values.deviceType && props.values.deviceType.deviceTypeInterface.length === 0) return;

    return <InterfacesList {...props} />;
  };

  const renderCoordinate = () => {
    return (
      <Grid size={{ xs: 6 }}>
        <Misc.NewPaperWithPadding>
          <Typography variant="h5" marginBottom={4} text={[<b>Coordinates</b>]} />
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" text={['Local']} />
            <Grid container direction="row" spacing={1}>
              <Grid>
                <TextInput
                  isReadOnly={isViewMode}
                  label="X"
                  type="number"
                  value={values.localCoordinates?.x}
                  onChangeValue={onChangeCoordinates('localCoordinates', 'x')}
                />
              </Grid>
              <Grid>
                <TextInput
                  isReadOnly={isViewMode}
                  label="Y"
                  type="number"
                  value={values.localCoordinates?.y}
                  onChangeValue={onChangeCoordinates('localCoordinates', 'y')}
                />
              </Grid>
              <Grid>
                <TextInput
                  isReadOnly={isViewMode}
                  label="Z"
                  type="number"
                  value={values.localCoordinates?.z}
                  onChangeValue={onChangeCoordinates('localCoordinates', 'z')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" text={['GNSS']} />
            <Grid container direction="row" spacing={1}>
              <Grid>
                <TextInput
                  isReadOnly={isViewMode}
                  label="Latitude"
                  type="number"
                  value={values.GPSCoordinates?.y}
                  onChangeValue={onChangeCoordinates('GPSCoordinates', 'y')}
                />
              </Grid>
              <Grid>
                <TextInput
                  isReadOnly={isViewMode}
                  label="Longitude"
                  type="number"
                  value={values.GPSCoordinates?.x}
                  onChangeValue={onChangeCoordinates('GPSCoordinates', 'x')}
                />
              </Grid>
              <Grid>
                <TextInput
                  isReadOnly={isViewMode}
                  label="Z"
                  type="number"
                  value={values.GPSCoordinates?.z}
                  onChangeValue={onChangeCoordinates('GPSCoordinates', 'z')}
                />
              </Grid>
            </Grid>
          </Grid>
        </Misc.NewPaperWithPadding>
      </Grid>
    );
  };

  const renderNewSensors = () => {
    const { sensors } = props.values;
    if (!sensors) return null;
    const nonVirtualSensors = sensors.filter(sensor => sensor.params.isVirtual === false);
    if (_.isEmpty(nonVirtualSensors)) return null;
    return (
      <Misc.NewPaperWithPadding>
        <Typography variant="h5" text={[<b>Sensors</b>]} marginBottom={6} />
        {renderOperationReadingTypes()}
      </Misc.NewPaperWithPadding>
    );
  };

  const renderOperationReadingTypes = () => {
    const { deviceType, sensors } = props.values;
    const deviceTypeCategories = _.get(deviceType, 'deviceTypeCategories');
    const isVirtual = _.isEmpty(deviceTypeCategories)
      ? false
      : deviceTypeCategories.find(dtc => dtc.category.logicalId === 'virtual');

    return (
      <Grid container spacing={4} alignItems="stretch" direction="row">
        {sensors.map((sensor, idx) => {
          if (sensor.params.isVirtual) {
            return;
          } else {
            const withSelect = !!isVirtual && sensor.selectedOperation && sensor.availableOperations;
            return (
              <Grid container spacing={2} size={{ xs: 12 }} key={`${idx}_row`}>
                <Grid size={{ xs: 12, md: withSelect ? 4 : 6 }}>
                  <TextInput isReadOnly label="Name" value={sensor.name} />
                </Grid>
                <Grid size={{ xs: 12, md: withSelect ? 4 : 6 }}>
                  <TextInput isReadOnly label="Reading type" value={sensor.readingType.name} />
                </Grid>
                <Grid size={{ xs: 12, md: withSelect ? 4 : 6 }}>
                  {withSelect && (
                    <Select
                      style={{ minWidth: '200px', marginTop: 15 }}
                      disabled={mode !== 'new'}
                      value={sensor.selectedOperation.logicalId}
                      onChange={handleChangeOperation(idx, sensor.availableOperations)}
                      options={sensor.availableOperations.map(item => ({ value: item.logicalId, label: item.name }))}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {sensor.streamParams && (
                    <JsonInput
                      isReadOnly={mode !== 'new'}
                      label={'Parameters'}
                      value={sensor.streamParams}
                      multiLine
                      onChange={handleChangeParam(idx)}
                    />
                  )}
                </Grid>
              </Grid>
            );
          }
        })}
      </Grid>
    );
  };

  const renderVersion = () => {
    return (
      <div>
        <Typography variant="h5" marginBottom={4} text={[<b>Version</b>]} />
        <Typography variant="h6" text={['Of equipment']} />
        <TextInput
          isReadOnly={isViewMode}
          label="Name"
          value={values.versionEquipmentName}
          onChangeValue={onCallbackChange('versionEquipmentName')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Value"
          value={values.versionEquipmentValue}
          onChangeValue={onCallbackChange('versionEquipmentValue')}
        />
        <Typography variant="h6" marginTop={2} text={['Of firmware']} />
        <TextInput
          isReadOnly={isViewMode}
          label="Name"
          value={values.versionFirmwareName}
          onChangeValue={onCallbackChange('versionFirmwareName')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Value"
          value={values.versionFirmwareValue}
          onChangeValue={onCallbackChange('versionFirmwareValue')}
        />
      </div>
    );
  };

  const renderConfigurationDeviceType = () => {
    if (isDeviceItemType || !props.values.deviceType || !props.values.deviceType.configurationFields) return null;

    const configurationFields = props.values.deviceType.configurationFields;

    let configValues = configurationFields.reduce((obj, item) => {
      obj[item.field] = props.values.configuration ? props.values.configuration[item.field] : item.default;
      return obj;
    }, {});

    const onChangeConfiguration = (field, value) => {
      configValues[field] = value;
      onCallbackChange('configuration')(configValues);
    };

    return (
      <Grid size={{ xs: 6 }}>
        <Misc.NewPaperWithPadding>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <h3>{t('Device configuration')}</h3>
            </Grid>
            {configurationFields.map((field, i) => {
              switch (field.type) {
                case 'number':
                  return (
                    <Grid size={{ xs: 12 }} key={i}>
                      <TextInput
                        isReadOnly={isViewMode}
                        label={field.name}
                        type={field.type}
                        required={field.required}
                        max={field.max}
                        min={field.min}
                        value={configValues[field.field]}
                        onChangeValue={v => onChangeConfiguration(field.field, v)}
                      />
                    </Grid>
                  );
                default:
                  break;
              }
            })}
          </Grid>
        </Misc.NewPaperWithPadding>
      </Grid>
    );
  };
  //#endregion

  useEffectAfterMount(() => {
    if (values.deviceType && values.deviceType.xDtRt) {
      const sensors = values.deviceType.xDtRt.map(xDtRt => {
        const sensorName = (xDtRt.params && xDtRt.params.sensorName) || xDtRt.readingType.name;
        const readingType = xDtRt.readingType;
        const availableOperations = xDtRt.readingType.xOperationReadingTypes.map(xOrT => {
          const operation = { ...xOrT.operation };
          operation.params = xOrT.params;
          return operation;
        });

        const defaultOperation = availableOperations.find(op => op.logicalId === 'equal');

        let streamParams;
        if (defaultOperation) {
          streamParams = defaultOperation.params;
        }
        if (!streamParams) {
          streamParams = xDtRt.params;
        }
        let params = xDtRt.params.params;

        return {
          name: sensorName,
          readingType,
          selectedOperation: defaultOperation,
          availableOperations,
          streamParams,
          params,
        };
      });
      if (!values.sensors) {
        onCallbackChange('sensors')(sensors);
      }
    }
  }, []);

  return (
    <Grid container size={{ xs: 12 }} rowSpacing={3} columnSpacing={1} marginBottom={3}>
      <Grid size={{ xs: 6 }}>
        <Misc.NewPaperWithPadding>{renderIdentifiers()}</Misc.NewPaperWithPadding>
      </Grid>
      {renderConfigurationDeviceType()}
      <Grid size={{ xs: 6 }}>
        <Misc.NewPaperWithPadding>{renderVersion()}</Misc.NewPaperWithPadding>
      </Grid>
      {!props.isItemType && (
        <>
          <Grid size={{ xs: showNewSensors ? 6 : 12 }}>{deviceTypeId && renderNewInterfaces()}</Grid>
          {showNewSensors && <Grid size={{ xs: deviceTypeId ? 6 : 12 }}>{showNewSensors && renderNewSensors()}</Grid>}
        </>
      )}
      {showDeviceSensors && renderSensorTable()}
      {showLocation && renderCoordinate()}
    </Grid>
  );
};

export default withTranslation()(DeviceForm);
